/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { dataService } from './dataService.ts';

export interface PushSubscriptionPreferences {
  criticalQuality: boolean;
  logisticsDelivered: boolean;
  billingApprovals: boolean;
  emergencySystem: boolean;
  soundEnabled: boolean;
}

export interface PersonnelPushSubscription {
  id: string;
  userId?: string;
  userName: string;
  userRole: string;
  endpoint: string;
  preferences: PushSubscriptionPreferences;
  subscribedAt: string;
  userAgent: string;
}

const STORAGE_KEY_SUB = 'snf_sic_push_subscription';
const STORAGE_KEY_PREFS = 'snf_sic_push_preferences';

export const DEFAULT_PREFERENCES: PushSubscriptionPreferences = {
  criticalQuality: true,
  logisticsDelivered: true,
  billingApprovals: true,
  emergencySystem: true,
  soundEnabled: true,
};

export class PushSubscriptionService {
  private static swRegistration: ServiceWorkerRegistration | null = null;

  // Initialize service worker for background push support
  static async initServiceWorker(): Promise<ServiceWorkerRegistration | null> {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      console.warn('[PushService] Service Worker no es soportado por este navegador.');
      return null;
    }

    try {
      // Register sw.js
      const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
      this.swRegistration = reg;
      console.log('[PushService] Service Worker registrado exitosamente:', reg.scope);
      return reg;
    } catch (error) {
      console.warn('[PushService] Error registrando Service Worker:', error);
      return null;
    }
  }

  // Get active Service Worker registration
  static async getRegistration(): Promise<ServiceWorkerRegistration | null> {
    if (this.swRegistration) return this.swRegistration;
    if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
      const reg = await navigator.serviceWorker.ready;
      this.swRegistration = reg;
      return reg;
    }
    return null;
  }

  // Check if Push API and ServiceWorker are supported
  static isPushSupported(): boolean {
    return (
      typeof window !== 'undefined' &&
      'serviceWorker' in navigator &&
      'Notification' in window
    );
  }

  // Get saved preferences
  static getPreferences(): PushSubscriptionPreferences {
    if (typeof window === 'undefined') return DEFAULT_PREFERENCES;
    try {
      const saved = localStorage.getItem(STORAGE_KEY_PREFS);
      return saved ? JSON.parse(saved) : DEFAULT_PREFERENCES;
    } catch {
      return DEFAULT_PREFERENCES;
    }
  }

  // Save preferences locally and sync to Firestore
  static async savePreferences(prefs: PushSubscriptionPreferences, currentUser?: { name?: string; role?: string }): Promise<void> {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY_PREFS, JSON.stringify(prefs));
    }
    
    // Update active subscription in local storage and database
    const currentSub = this.getSubscriptionLocally();
    if (currentSub) {
      currentSub.preferences = prefs;
      localStorage.setItem(STORAGE_KEY_SUB, JSON.stringify(currentSub));

      try {
        await dataService.saveDocument('push_subscriptions', currentSub);
      } catch (e) {
        console.warn('[PushService] No se pudo actualizar suscripción en la base de datos:', e);
      }
    }
  }

  // Check if personnel is currently subscribed locally
  static isSubscribedLocally(): boolean {
    if (typeof window === 'undefined') return false;
    const sub = localStorage.getItem(STORAGE_KEY_SUB);
    const fcmTok = localStorage.getItem('snf_sic_fcm_token');
    return !!(sub || fcmTok);
  }

  // Get current local subscription object
  static getSubscriptionLocally(): PersonnelPushSubscription | null {
    if (typeof window === 'undefined') return null;
    try {
      const raw = localStorage.getItem(STORAGE_KEY_SUB);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  // Subscribe personnel to push notifications
  static async subscribePersonnel(
    userName: string = 'Personal Operativo',
    userRole: string = 'OPERADOR',
    prefs: PushSubscriptionPreferences = DEFAULT_PREFERENCES
  ): Promise<PersonnelPushSubscription | null> {
    // 1. Request notification permission if supported
    let permission: string = 'granted';
    if (typeof window !== 'undefined' && 'Notification' in window) {
      try {
        permission = Notification.permission;
        if (permission !== 'granted') {
          permission = await Notification.requestPermission();
        }
      } catch (err) {
        console.warn('[PushService] Solicitud de permiso en navegador restringida (iframe/sandbox):', err);
        permission = 'granted'; // Default to enabled in-app mode
      }
    }

    // 2. Register Service Worker
    const reg = await this.initServiceWorker().catch(() => null);

    // Generate or get push subscription endpoint
    let pushEndpoint = `https://fcm.googleapis.com/fcm/send/snf_sic_device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    if (reg && reg.pushManager) {
      try {
        let existingSub = await reg.pushManager.getSubscription();
        if (!existingSub) {
          const applicationServerKey = new Uint8Array([
            0x04, 0x1f, 0x2a, 0x3b, 0x4c, 0x5d, 0x6e, 0x7f,
            0x80, 0x91, 0xa2, 0xb3, 0xc4, 0xd5, 0xe6, 0xf7,
            0x08, 0x19, 0x2a, 0x3b, 0x4c, 0x5d, 0x6e, 0x7f,
            0x80, 0x91, 0xa2, 0xb3, 0xc4, 0xd5, 0xe6, 0xf7,
            0x01, 0x02, 0x03, 0x04, 0x05, 0x06, 0x07, 0x08,
            0x09, 0x0a, 0x0b, 0x0c, 0x0d, 0x0e, 0x0f, 0x10,
            0x11, 0x12, 0x13, 0x14, 0x15, 0x16, 0x17, 0x18, 0x19
          ]);
          existingSub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey
          }).catch(err => {
            console.warn('[PushService] Error con VAPID PushManager subscription, usando endpoint local:', err);
            return null;
          });
        }
        if (existingSub) {
          pushEndpoint = existingSub.endpoint;
        }
      } catch (err) {
        console.warn('[PushService] Error interactuando con PushManager:', err);
      }
    }

    const subscriptionObj: PersonnelPushSubscription = {
      id: `SUB-${Date.now()}`,
      userName,
      userRole,
      endpoint: pushEndpoint,
      preferences: prefs,
      subscribedAt: new Date().toISOString(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Browser'
    };

    // Save locally
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY_SUB, JSON.stringify(subscriptionObj));
      localStorage.setItem(STORAGE_KEY_PREFS, JSON.stringify(prefs));
    }

    // Save in Firestore for administrative dispatch
    try {
      await dataService.saveDocument('push_subscriptions', subscriptionObj);
    } catch (err) {
      console.warn('[PushService] No se pudo guardar suscripción en Firestore:', err);
    }

    return subscriptionObj;
  }

  // Unsubscribe personnel
  static async unsubscribePersonnel(): Promise<boolean> {
    const localSub = this.getSubscriptionLocally();
    if (localSub) {
      try {
        await dataService.deleteDocument('push_subscriptions', localSub.id);
      } catch (e) {
        console.warn('[PushService] No se pudo eliminar de Firestore:', e);
      }
    }

    if (this.swRegistration && this.swRegistration.pushManager) {
      try {
        const sub = await this.swRegistration.pushManager.getSubscription();
        if (sub) {
          await sub.unsubscribe();
        }
      } catch (e) {
        console.warn('[PushService] Error al dar de baja en PushManager:', e);
      }
    }

    localStorage.removeItem(STORAGE_KEY_SUB);
    localStorage.removeItem('snf_sic_fcm_token');
    return true;
  }

  // Trigger a native background push notification via ServiceWorker registration
  static async triggerBackgroundPush(
    title: string,
    body: string,
    category: keyof PushSubscriptionPreferences = 'criticalQuality',
    urlTarget: string = '/'
  ): Promise<boolean> {
    const prefs = this.getPreferences();
    if (category in prefs && !(prefs as any)[category]) {
      console.log(`[PushService] Notificación omitida según preferencias (${category}=false)`);
      return false;
    }

    const reg = await this.getRegistration();

    const options: any = {
      body,
      icon: 'https://www.grupoisn.com.mx/wp-content/uploads/2025/12/cropped-WhatsApp-Image-2025-12-19-at-12.31.53.jpeg',
      badge: 'https://www.grupoisn.com.mx/wp-content/uploads/2025/12/cropped-WhatsApp-Image-2025-12-19-at-12.31.53.jpeg',
      tag: `snf-push-${Date.now()}`,
      vibrate: [200, 100, 200, 100, 400],
      requireInteraction: category === 'criticalQuality' || category === 'emergencySystem',
      data: {
        url: urlTarget,
        timestamp: Date.now(),
        category
      },
      actions: [
        { action: 'open', title: '👁️ Ver Detalle' },
        { action: 'dismiss', title: '✖️ Entendido' }
      ]
    };

    if (reg && reg.showNotification) {
      try {
        await reg.showNotification(title, options);
        return true;
      } catch (err) {
        console.warn('[PushService] SW showNotification falló, usando fallback de ventana:', err);
      }
    }

    // Fallback if ServiceWorker showNotification is restricted
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      try {
        new Notification(title, options);
        return true;
      } catch (e) {
        console.warn('[PushService] Native Notification fallback error:', e);
      }
    }

    return false;
  }
}
